import React, { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import WorkspaceHeader from "@/components/layout/WorkspaceHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { postBetaSignup } from "@/lib/backend";
import StarryBackground from "@/components/layout/StarryBackground";
import { useSignIn } from "@clerk/clerk-react";
import { enterDemoMode } from "@/lib/demoMode";

type SubmitState = "idle" | "loading" | "success" | "error";

type BetaSignupSplashProps = {
  onClose?: () => void;
};

export default function BetaSignupSplash({ onClose }: BetaSignupSplashProps) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubmitState>("idle");
  const [error, setError] = useState<string | null>(null);
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const [demoSigningIn, setDemoSigningIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Please enter a valid email address.");
      setState("error");
      return;
    }

    setState("loading");
    setError(null);
    try {
      await postBetaSignup({ email: trimmed });
      setState("success");
    } catch (err: any) {
      setError(err?.message || "Unable to submit right now.");
      setState("error");
    }
  };

  const handlePlayground = async () => {
    if (!signInLoaded || !signIn || !setActive) {
      setError("Auth is still loading. Please try again in a moment.");
      setState("error");
      return;
    }

    setDemoSigningIn(true);
    setError(null);
    try {
      // Reset workspace WindowFrame/layout state for a clean demo start.
      try {
        window.localStorage.removeItem("workspace:layout:v4");
        window.localStorage.removeItem("workspace:layout");
      } catch {
        // ignore storage errors
      }

      // Mark this browser session as “demo mode” BEFORE signing in
      // so downstream components can gate token-expensive features.
      enterDemoMode();

      const demoEmail =
        ((import.meta as any)?.env?.VITE_DEMO_EMAIL as string | undefined) ||
        "calcerademo@gmail.com";
      const demoPassword =
        ((import.meta as any)?.env?.VITE_DEMO_PASSWORD as string | undefined) ||
        "CalceraDemo25!";

      const res = await signIn.create({
        identifier: demoEmail,
        password: demoPassword,
      });

      if (res.status === "complete" && res.createdSessionId) {
        await setActive({ session: res.createdSessionId });
        onClose?.();
        return;
      }

      // If Clerk requires additional factors, we treat this as an error for the demo flow.
      setError("Unable to sign in to the demo account (unexpected auth state).");
      setState("error");
    } catch (e: any) {
      setError(e?.message || "Unable to sign in to the demo account.");
      setState("error");
    } finally {
      setDemoSigningIn(false);
    }
  };


  const disabled = state === "loading" || state === "success" || demoSigningIn;

  return (
    <SidebarProvider className="w-svw">
      <div className="fixed inset-0 z-50 flex h-svh w-svw flex-col overflow-hidden bg-transparent text-white">
        <StarryBackground
          variant="full"
          showParticles={true}
          showPrompts={false}
        />

        <WorkspaceHeader />

        <main className="relative flex-1">
          <div className="relative z-10 flex h-full w-full min-h-[calc(100svh-56px)] items-center justify-center px-6 py-12">
            <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-black/30 p-8 text-center shadow-2xl backdrop-blur-md">
              <p className="mb-6 text-lg font-semibold">
                Want to sign up for our beta? Drop your email below!
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={disabled}
                    className="bg-white/5 text-white placeholder:text-white/60"
                  />
                  <Button
                    type="submit"
                    disabled={disabled}
                    className="w-full sm:w-auto"
                  >
                    {state === "success" ? "Submitted" : "Notify me"}
                  </Button>
                </div>

                {state === "success" && (
                  <div className="text-sm text-emerald-300">
                    Thanks! You're on the list!
                  </div>
                )}

                {error && (
                  <div className="text-sm text-red-300">{error}</div>
                )}
              </form>

              {/* Divider */}
              <div className="my-6 border-t border-white/10" />

              {/* Playground */}
              <p className="mb-2 text-xs text-white/60">
                Or explore Calcera instantly:
              </p>

              <Button
                variant="outline"
                onClick={handlePlayground}
                className="w-full border-purple-500/50 hover:bg-purple-500/10"
                disabled={demoSigningIn}
              >
                {demoSigningIn ? "Signing in…" : "Enter Playground"}
              </Button>

              <p className="mt-2 text-[11px] text-white/40">
                Playground mode - changes will not be saved
              </p>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
